import React, { useState } from 'react'
import { Alert, Button } from '@mui/material'
import { createCarePlanIfMissing } from '../../api/useSharedCarePlan'
import { CARE_PLAN_TAB_ID, useTabNavigation } from '../../dashboard/tabNavigationContext'
import { createIntakeCarePlan } from './intakeCarePlan'

type Outcome = 'creating' | 'created' | 'exists' | 'failed'

export const useIntakeCarePlan = (uid: string) => {
  const [outcome, setOutcome] = useState<Outcome | null>(null)

  const create = async () => {
    setOutcome('creating')
    try {
      const created = await createCarePlanIfMissing(uid, 'psychiatrist', {
        ...createIntakeCarePlan(),
        status: 'draft',
        reviewPending: true,
      })
      setOutcome(created ? 'created' : 'exists')
    } catch {
      setOutcome('failed')
    }
  }

  return { outcome, create }
}

type IntakeCarePlan = ReturnType<typeof useIntakeCarePlan>

export const IntakeCarePlanNotice = ({ plan }: { plan: IntakeCarePlan }) => {
  const { openTab, hasTab } = useTabNavigation()
  const review = hasTab(CARE_PLAN_TAB_ID) && (
    <Button color="inherit" size="small" onClick={() => openTab(CARE_PLAN_TAB_ID)}>
      Review care plan
    </Button>
  )

  if (plan.outcome === 'created') {
    return (
      <Alert severity="success" action={review}>
        Care plan created from this intake note. It&apos;s a draft until you review and save it.
      </Alert>
    )
  }
  if (plan.outcome === 'exists') {
    return (
      <Alert severity="info" action={review}>
        This patient already has a care plan, so it was left as it is.
      </Alert>
    )
  }
  if (plan.outcome === 'failed') {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={plan.create}>
            Retry
          </Button>
        }
      >
        The intake note was saved, but the care plan couldn&apos;t be created.
      </Alert>
    )
  }

  return null
}
